// packages/web/src/lib/api/addresses.ts
// Address management API functions

import { apiFetch } from './fetch-helper';

export interface UserAddress {
  id: string;
  user_id: string;
  label?: string;
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  latitude?: number;
  longitude?: number;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateAddressRequest {
  label?: string;
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  country?: string;
}

export interface UpdateAddressRequest extends Partial<CreateAddressRequest> {
  is_default?: boolean;
}

// Get all user addresses
export async function getUserAddresses(): Promise<{ addresses: UserAddress[]; count: number }> {
  return apiFetch('/api/customer/addresses');
}

// Create a new address
export async function createUserAddress(address: CreateAddressRequest): Promise<UserAddress> {
  return apiFetch('/api/customer/addresses', {
    method: 'POST',
    body: JSON.stringify(address),
  });
}

// Update an address
export async function updateUserAddress(id: string, address: UpdateAddressRequest): Promise<UserAddress> {
  return apiFetch(`/api/customer/addresses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(address),
  });
}

// Delete an address
export async function deleteUserAddress(id: string): Promise<void> {
  return apiFetch(`/api/customer/addresses/${id}`, {
    method: 'DELETE',
  });
}

// Set an address as default
export async function setDefaultAddress(id: string): Promise<UserAddress> {
  return apiFetch(`/api/customer/addresses/${id}/default`, {
    method: 'POST',
  });
}

// IP-based geolocation (for anonymous users)
export interface GeoLocation {
  city: string;
  region: string; // state
  postal: string; // zip
  country: string;
  latitude: number;
  longitude: number;
}

export async function getLocationFromIP(): Promise<GeoLocation | null> {
  // Try multiple geolocation services with fallbacks
  const services = [
    // Internal API endpoint (if available)
    {
      url: '/api/geolocation/ip',
      internal: true,
      parseResponse: (data: any) => ({
        city: data.city || '',
        region: data.region || data.state || '',
        postal: data.postal || data.zip || '',
        country: data.country || 'US',
        latitude: parseFloat(data.latitude) || 0,
        longitude: parseFloat(data.longitude) || 0,
      })
    },
    // Alternative external service (ipify + ipapi)
    {
      url: 'https://api.ipify.org?format=json',
      internal: false,
      parseResponse: async (ipData: any) => {
        try {
          // Get IP first, then get location data
          const ipResponse = await fetch(`https://ipapi.co/${ipData.ip}/json/`);
          if (!ipResponse.ok) throw new Error('IP location lookup failed');

          const locationData = await ipResponse.json();
          return {
            city: locationData.city || '',
            region: locationData.region || '',
            postal: locationData.postal || '',
            country: locationData.country_name || 'US',
            latitude: parseFloat(locationData.latitude) || 0,
            longitude: parseFloat(locationData.longitude) || 0,
          };
        } catch (error) {
          console.warn('Secondary geolocation service failed:', error);
          return null;
        }
      }
    },
    // Fallback: return US defaults
    {
      url: null,
      internal: true,
      parseResponse: () => ({
        city: '',
        region: '',
        postal: '',
        country: 'US',
        latitude: 39.8283, // Center of US
        longitude: -98.5795,
      })
    }
  ];

  for (const service of services) {
    try {
      if (!service.url) {
        // Fallback service - always succeeds
        console.log('[GEOLOCATION] Using fallback US location');
        return service.parseResponse(null);
      }

      console.log(`[GEOLOCATION] Trying service: ${service.internal ? 'internal' : 'external'} ${service.url}`);

      const response = await fetch(service.url, {
        method: 'GET',
        // Only set CORS mode for external services
        ...(service.internal ? {} : { mode: 'cors' })
      });

      if (!response.ok) {
        console.warn(`[GEOLOCATION] Service ${service.url} returned ${response.status}`);
        continue;
      }

      const data = await response.json();

      if (typeof service.parseResponse === 'function') {
        const result = await service.parseResponse(data);
        if (result) {
          console.log(`[GEOLOCATION] Success with ${service.internal ? 'internal' : 'external'} service`);
          return result;
        }
      } else {
        // Legacy parsing for backward compatibility
        const result = {
          city: data.city || '',
          region: data.region || data.state || '',
          postal: data.postal || data.zip || '',
          country: data.country_name || data.country || 'US',
          latitude: parseFloat(data.latitude) || 0,
          longitude: parseFloat(data.longitude) || 0,
        };
        console.log(`[GEOLOCATION] Success with legacy parsing`);
        return result;
      }

    } catch (error) {
      console.warn(`[GEOLOCATION] Service ${service.url} failed:`, error.message);
      continue;
    }
  }

  console.warn('[GEOLOCATION] All geolocation services failed, returning null');
  return null;
}